import useCurrentWalletConnectionState from 'src/hooks/useCurrentWalletConnectionState';
import { WalletToDisplayInfo } from 'src/hooks/useWalletsToDisplay';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { cj } from 'src/utils/classNames';
import ArcLoadingSpinner from '../Icons/LoadingSpinner/ArcLoadingSpinner';
import Tag from '../primitives/Tag/Tag';
import { WalletReadyState } from '@solana/wallet-adapter-base';

export const WALLET_BUTTON_CLASSNAME = cj(
	'flex w-full items-center justify-between rounded-sm p-lg',
	'bg-input-bg typo-t3 text-text-default hover:bg-button-disabled'
);

export const WALLET_PROVIDER_BG_CLASSNAME: Record<string, string> = {
	MathWallet: 'bg-[#000]',
};

export const walletsToAddPaddingFor = ['MathWallet', 'WalletConnect'];

const walletsToHideDetectedTextFor = ['WalletConnect'];

const checkShowDetectedLabel = (wallet: WalletToDisplayInfo) => {
	const walletProvider = wallet.provider;

	if (
		walletProvider.readyState === WalletReadyState.Loadable &&
		walletProvider.adapter.name === 'Solflare'
	) {
		return false;
	}

	if (walletsToHideDetectedTextFor.includes(walletProvider.adapter.name)) {
		return false;
	}

	return walletProvider.readyState === WalletReadyState.Installed;
};

export const WalletButton = (props: {
	wallet: WalletToDisplayInfo;
	onChooseWallet?: (selection: string) => void;
	hideDetectedLabel?: boolean;
}) => {
	const currentlySelectedWallet = useDriftStore((s) => s.wallet.current);
	const walletConnectionState = useCurrentWalletConnectionState();
	const walletProvider = props.wallet.provider;
	const suggestedHighlight = props?.wallet?.suggestedHighlight;

	const isConnecting =
		walletConnectionState.instance.is('Connecting') &&
		currentlySelectedWallet.adapter?.name === walletProvider.adapter.name;

	const walletProviderBgClassName = (() => {
		const { name } = walletProvider.adapter;
		if (!(name in WALLET_PROVIDER_BG_CLASSNAME)) {
			return;
		}

		return WALLET_PROVIDER_BG_CLASSNAME[name];
	})();

	return (
		<button
			className={WALLET_BUTTON_CLASSNAME}
			onClick={() => props.onChooseWallet?.(walletProvider.adapter.name ?? '')}
			key={walletProvider.adapter.name}
			data-testid={`connect-wallet-button-${walletProvider.adapter.name}`}
		>
			<div className="flex w-full items-center justify-between">
				<div className="flex items-center gap-x-md">
					<div
						className={cj(
							'size-8',
							walletsToAddPaddingFor.includes(walletProvider.adapter.name) &&
								'p-xs',
							walletProviderBgClassName
						)}
					>
						<img src={walletProvider.adapter.icon} className="size-full" />
					</div>
					<span className="text-left">{walletProvider.adapter.name}</span>
					{suggestedHighlight && (
						<Tag className="bg-warn-orange text-black">Suggested</Tag>
					)}
				</div>
				{isConnecting ? (
					<div className="flex items-center gap-x-xs text-text-secondary">
						<ArcLoadingSpinner />
						<span className="typo-b4">Connecting...</span>
					</div>
				) : (
					!props.hideDetectedLabel &&
					checkShowDetectedLabel(props.wallet) && (
						<span className="typo-b4 text-text-secondary">Detected</span>
					)
				)}
			</div>
		</button>
	);
};
